/**
 * Reusable stateless form component for StudyUser
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// import third-party libraries
import _ from 'lodash';
import { DateTime } from 'luxon';

// import form components
import {
  CheckboxInput
  , EmailInput
  , ListComparator
  , RadioInput
  , SelectFromArray
  , SelectFromObject
  , SingleDatePickerInput
  , TextInput
} from '../../../global/components/forms';

const StudyUserForm = ({
  loggedInUser
  , availableCourses
  // , availableRoles
  , cancelLink
  , courseMap
  , formHelpers
  , formTitle
  , formType
  , handleFormChange
  , handleFormSubmit
  , sponsor
  , statuses
  , study
  , studySites
  , studyUser
  , studyRoles
  , viewingAs
}) => {

  // set the button text
  const buttonText = formType === "create" ? "Create Program Permission" : "Update Program Permission";

  // set the form header
  const header = formTitle ? <div className="formHeader"><h2> {formTitle} </h2><hr/></div> : <div/>;

  return (
    <div className="yt-container">
      <div className="yt-row center-horiz">
        <div className="form-container -slim">
          <form name="studyUserForm" className="studyUser-form" onSubmit={handleFormSubmit}>
            {header}
            <div className="-form-step"><span className="-num">1</span> Sponsor/Program/Course info</div>
            <p><strong>Sponsor:</strong> {sponsor ? sponsor.name : ''}</p>
            <p><strong>Program:</strong> {study ? study.name : ''}</p>
            <div className="yt-col full m_60">
              { viewingAs === 'admin' ?
                <SelectFromObject
                  change={handleFormChange}
                  clearable={false}
                  display={'title'}
                  filterable={true}
                  helpText="When a user starts a module, they are/were automatically assigned to a course. This will override that."
                  label="Course:"
                  name="studyUser._course"
                  objects={availableCourses}
                  placeholder="-- Select a Course --"
                  required={false}
                  selected={studyUser._course}
                  value={'_id'}
                />
              :
              <p><strong>Course: </strong> {studyUser._course && courseMap[studyUser._course] ? courseMap[studyUser._course].title : <em>waiting for user to start</em>}</p>
            }
            </div>
            <div className="-form-step"><span className="-num">2</span> Site/Team info</div>
            <div className="yt-col full m_60">
              { studyUser._user && studyUser._user.length > 0 ?
                <p><strong>User Email:</strong> {studyUser.email}</p>
                :
                <EmailInput
                  change={handleFormChange}
                  label="Email Address"
                  name="studyUser.email"
                  placeholder="Email"
                  required={true}
                  value={studyUser.email}
                />
              }
            </div>
              <RadioInput
                label="Link site/team number"
                options={[
                  {val: 'sites', display: 'from a list of existing sites'},
                  {val: 'freetext', display: 'by adding a free-text site/team number'},
                ]}
                name="formHelpers.linkSiteFrom"
                value={formHelpers.linkSiteFrom}
                change={handleFormChange}
                inLine={true}
              />
            <div className="yt-col full m_60">
              { formHelpers.linkSiteFrom === 'freetext' ?
                <TextInput
                  change={handleFormChange}
                  helpText="This will store a free text site/team number which will match automatically IF/WHEN it exists in the databse"
                  name="studyUser.siteNumber"
                  required={false}
                  value={studyUser.siteNumber ? studyUser.siteNumber : ''}
                />
                :
                <SelectFromObject
                  change={handleFormChange}
                  clearable={false}
                  display={'number'}
                  filterable={true}
                  helpText="This will automatically match this program user with a site/team in the database."
                  name="studyUser.siteNumber"
                  objects={studySites}
                  placeholder="-- Select a Site/Team Number --"
                  required={false}
                  selected={studyUser.siteNumber}
                  value={'number'}
                />
              }
            </div>
            { studyUser.email && studyUser.email.length > 0 ?
                <div>
                  <div className="-form-step"><span className="-num">3</span>Manage Access & Permissions</div>
                  <div className="yt-col full m_40 l_30 xl_25">
                    <SelectFromArray
                      change={handleFormChange}
                      helpText={
                        studyUser.status === 'active' ?
                          <span>User can access this program.</span>
                        : studyUser.status === 'expired' ?
                          <span>User's access has expired.</span>
                        : studyUser.status === 'deactivated' ?
                          <span>User's access revoked by admin</span>
                        :
                          <span>Manage user's access to this program.</span>
                      }
                      items={statuses}
                      label="Access status"
                      name="studyUser.status"
                      placeholder="-- Select a Status --"
                      value={studyUser.status}
                    />
                  </div>
                  {/* <RadioInput
                    label="Does their access expire?"
                    helpText={formHelpers.doesExpire === 'yes' ? 'This user will be unable to access this study after the following expiration date:' : 'This user will be able to access this study unless a Pro-ficiency or Study admin deactivates their account.'}
                    options={[
                      {val: 'yes', display: 'Yes'},
                      {val: 'no', display: 'No'},
                    ]}
                    name="formHelpers.doesExpire"
                    value={formHelpers.doesExpire}
                    change={handleFormChange}
                    inLine={true}
                  />
                  { formHelpers.doesExpire === 'yes' ?
                    <SingleDatePickerInput
                      change={handleFormChange}
                      label="Expiration Date"
                      initialDate={formHelpers.expires}
                      name='formHelpers.expires'
                      numberOfMonths={1}
                    />
                  :
                  null
                  } */}
                  <CheckboxInput
                    change={handleFormChange}
                    checked={studyUser.admin}
                    label="Is this user an Admin of the selected Program?"
                    name="studyUser.admin"
                    value={studyUser.admin}
                  />
                  {/* should we add a select dropdown for permissions? */}
                  {/* <CheckboxInput
                    change={handleFormChange}
                    checked={studyUser.viewAnalytics}
                    disabled={studyUser.admin}
                    label="Is this user allowed to view analytics/reports for the selected Program?"
                    name="studyUser.viewAnalytics"
                    tooltipText={"Program users with Admin access can also view reports."}
                    value={studyUser.viewAnalytics}
                  /> */}
                  <div className="-form-step"><span className="-num">4</span>Manage Role</div>
                  <div className="yt-col full m_60">
                    <SelectFromObject
                      change={handleFormChange}
                      clearable={true}
                      display={'role'}
                      filterable={true}
                      label="User role for this program"
                      name="studyUser._groupRole"
                      objects={studyRoles}
                      placeholder="-- Select a role --"
                      selected={studyUser._groupRole}
                      value={'_id'}
                      required={true}
                    />
                  </div>
                </div>
              :
                null
            }
            <div className="input-group">
              <div className="yt-row space-between">
                <Link className="yt-btn link" to={cancelLink}>Cancel</Link>
                <button className="yt-btn" type="submit" > {buttonText} </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

StudyUserForm.propTypes = {
  availableCourses: PropTypes.array
  // , availableRoles: PropTypes.array
  , cancelLink: PropTypes.string.isRequired
  , courseMap: PropTypes.object
  , formHelpers: PropTypes.object
  , formTitle: PropTypes.string
  , formType: PropTypes.string.isRequired
  , handleFormChange: PropTypes.func.isRequired
  , handleFormSubmit: PropTypes.func.isRequired
  , sponsor: PropTypes.object.isRequired
  , statuses: PropTypes.array.isRequired
  , study: PropTypes.object.isRequired
  , studySites: PropTypes.array
  , studyUser: PropTypes.object.isRequired
  , studyRoles: PropTypes.array
  , viewingAs: PropTypes.string
}

StudyUserForm.defaultProps = {
  availableCourses: []
  , availableRoles: []
  , courseMap: {}
  , formHelpers: {}
  , formTitle: ''
  , studySites: []
  , viewingAs: 'studyUserAdmin'
}

export default StudyUserForm;



// WEBPACK FOOTER //
// modules/studyUser/components/StudyUserForm.js.jsx